import React, { useState } from 'react';
import { Building2, TrendingUp, ChevronDown, ChevronUp, ExternalLink, Users, Sparkles } from 'lucide-react';
import { PIPELINE_PROJECTS, PIPELINE_PHASES } from '../constants/content';
import { FloatingAdinkra, KenteClothStrip } from '../components/Decorators';
import { RevealOnScroll } from '../hooks/useScrollReveal';

const iconMap = { TrendingUp, Building2 };

/* ── Phase Timeline ── */
const PhaseTimeline = ({ activeIndex }) => (
  <div className="phase-timeline">
    {PIPELINE_PHASES.map((phase, i) => (
      <div key={phase} className={`phase-step ${i <= activeIndex ? 'active' : ''} ${i === activeIndex ? 'current' : ''}`}>
        <div className="phase-dot" />
        {i < PIPELINE_PHASES.length - 1 && <div className="phase-line" />}
        <span className="phase-label">{phase}</span>
      </div>
    ))}
  </div>
);

/* ── Community Model (Susu) Card ── */
const CommunityModelCard = ({ model }) => (
  <RevealOnScroll direction="scale" delay={200}>
    <div className="community-model-card">
      <div className="community-model-header">
        <Users size={20} color="var(--gold)" />
        <h4>{model.title}</h4>
      </div>
      <p className="community-model-desc">{model.description}</p>
      <div className="community-pillars">
        {model.pillars.map((p, i) => (
          <RevealOnScroll key={p.label} delay={i * 150} direction="up">
            <div className="community-pillar">
              <span className="pillar-label">{p.label}</span>
              <p className="pillar-detail">{p.detail}</p>
            </div>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </RevealOnScroll>
);

/* ── Single Pipeline Project Card ── */
const PipelineProjectCard = ({ project, index }) => {
  const [showOfferings, setShowOfferings] = useState(false);
  const Icon = iconMap[project.icon] || TrendingUp;

  return (
    <RevealOnScroll delay={index * 200} direction={index % 2 === 0 ? 'left' : 'right'}>
      <div className="pipeline-card">
        {/* Status + Phase */}
        <div className="pipeline-card-top">
          <div className={`pipeline-status ${project.phase === 'Concept' ? 'concept' : ''}`}>
            {project.phase === 'Concept' ? 'Concept Stage' : 'In Development'}
          </div>
          <PhaseTimeline activeIndex={project.phaseIndex} />
        </div>

        {/* Icon + Title + Tagline */}
        <div className="pipeline-title-row">
          <div className="pipeline-icon-wrap">
            <Icon size={28} />
          </div>
          <div>
            <h3 className="pipeline-project-title">{project.title}</h3>
            <p className="pipeline-tagline">{project.tagline}</p>
          </div>
        </div>

        {/* Vision */}
        <p className="pipeline-vision">{project.vision}</p>

        {/* Key Offerings — expandable */}
        <button className="pipeline-expand-btn" onClick={() => setShowOfferings(!showOfferings)}>
          {showOfferings ? 'Hide Offerings' : 'View Key Offerings'}
          {showOfferings ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showOfferings && (
          <div className="pipeline-offerings-grid">
            {project.keyOfferings.map((o, i) => (
              <RevealOnScroll key={o.title} delay={i * 100} direction="scale">
                <div className="pipeline-offering">
                  <Sparkles size={14} color="var(--gold)" style={{ flexShrink: 0, marginTop: '0.2rem' }} />
                  <div>
                    <h5 className="offering-title">{o.title}</h5>
                    <p className="offering-desc">{o.desc}</p>
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        )}

        {/* Community Model (SME Hub only) */}
        {project.communityModel && (
          <CommunityModelCard model={project.communityModel} />
        )}

        {/* Target Market */}
        <RevealOnScroll direction="left" delay={100}>
          <div className="pipeline-target">
            <span className="target-label">Target Market</span>
            <p>{project.targetMarket}</p>
          </div>
        </RevealOnScroll>

        {/* Differentiators */}
        <RevealOnScroll direction="right" delay={200}>
          <div className="pipeline-differentiators">
            <span className="diff-label">Key Differentiators</span>
            <ul>
              {project.differentiators.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          </div>
        </RevealOnScroll>

        {/* Proof of Concept (Real Estate Hub only) */}
        {project.proofOfConcept && (
          <RevealOnScroll direction="scale" delay={300}>
            <div className="pipeline-proof">
              <div className="proof-badge">Proof of Concept</div>
              <h5>{project.proofOfConcept.title}</h5>
              <p>{project.proofOfConcept.description}</p>
              {project.proofOfConcept.link && (
                <a href={project.proofOfConcept.link} target="_blank" rel="noopener noreferrer" className="project-link">
                  Visit Live Site <ExternalLink size={12} />
                </a>
              )}
            </div>
          </RevealOnScroll>
        )}
      </div>
    </RevealOnScroll>
  );
};

/* ── Page ── */
const PipelinePage = () => (
  <div className="adinkra-texture-bg" style={{ padding: '4rem 2rem', position: 'relative' }}>
    <FloatingAdinkra count={6} color="rgba(201, 168, 76, 0.05)" />
    <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
      <RevealOnScroll>
        <div className="section-header">
          <h2 className="section-title">Pipeline Projects</h2>
          <p className="section-subtitle">
            Ventures in development — building the future of African enterprise
          </p>
        </div>
        <div className="kente-border"></div>
      </RevealOnScroll>

      <div className="pipeline-grid">
        {PIPELINE_PROJECTS.map((project, index) => (
          <PipelineProjectCard key={project.id} project={project} index={index} />
        ))}
      </div>

      <KenteClothStrip height={24} className="pipeline-kente-divider" />

      {/* Roadmap summary */}
      <RevealOnScroll direction="scale">
        <div className="pipeline-roadmap">
          <h3 className="roadmap-title">The Roadmap</h3>
          <div className="roadmap-steps">
            {[
              { phase: 'Now', text: 'The Merchant Hub in active development — founder onboarding framework and marketplace architecture.' },
              { phase: 'Next', text: 'Avenue Lincoln proof of concept validates the Real Estate Management Hub model with live bookings.' },
              { phase: 'Future', text: 'Both platforms converge — SME founders access property services, property clients discover SME products.' },
            ].map((step, i) => (
              <RevealOnScroll key={i} delay={i * 200} direction="up">
                <div className="roadmap-step">
                  <span className="roadmap-phase">{step.phase}</span>
                  <p>{step.text}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </RevealOnScroll>
    </div>
  </div>
);

export default PipelinePage;
